<script setup lang="ts">
import { UsersRound } from "@lucide/vue";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { type RouteLocationNormalizedLoaded, RouterLink, useRoute } from "vue-router";

import { MASTER_SECTION_IDS, type MasterSectionId } from "@/entities/master";
import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormAlert,
  PageCTA,
  Skeleton,
} from "@/shared/ui";

import { useMastersListStore } from "../model/store";
import MasterListRow from "./MasterListRow.vue";

/**
 * Композит списку майстрів (spec §4.2/§8.1): один `Card` зі станами
 * loading (Skeleton) / error (FormAlert+retry) / empty (CTA) / ready (список
 * `MasterListRow`). Сторінка лишається тонкою; `onMounted → load()` гарантує
 * re-fetch на кожен вхід у `/masters` (інвалідація S7).
 */
const { t } = useI18n();
// useRoute() may return undefined in test environments without router plugin.
const route = useRoute() as RouteLocationNormalizedLoaded | undefined;
const store = useMastersListStore();

const validSetup = computed(() => {
  const s = route?.query.setup;
  return typeof s === "string" && (MASTER_SECTION_IDS as readonly string[]).includes(s)
    ? (s as MasterSectionId)
    : undefined;
});

const targetHash = computed(() =>
  validSetup.value ? `#${validSetup.value}` : undefined,
);

const setupHint = computed(() => {
  if (!validSetup.value) return "";
  return t(`masters.setupHint.${validSetup.value}`);
});

onMounted(() => {
  void store.load();
});
</script>

<template>
  <FormAlert
    v-if="validSetup && store.listStatus === 'ready' && !store.isEmpty"
    variant="info"
    :message="setupHint"
    class="mb-4"
  />
  <Card data-testid="masters-list">
    <CardHeader>
      <CardTitle>{{ t("masters.title") }}</CardTitle>
      <CardDescription>{{ t("masters.subtitle") }}</CardDescription>
      <CardAction v-if="store.listStatus === 'ready' && !store.isEmpty">
        <Button
          as-child
          size="sm"
          class="max-md:hidden"
        >
          <RouterLink
            data-testid="btn-add-master-desktop"
            :to="{ name: 'master-create' }"
          >
            {{ t("masters.add") }}
          </RouterLink>
        </Button>
      </CardAction>
    </CardHeader>

    <CardContent>
      <!-- visually-hidden polite статус-регіон: озвучує success без toast-спаму -->
      <p
        class="sr-only"
        role="status"
        aria-live="polite"
      >
        {{ store.politeMessage }}
      </p>

      <!-- loading: skeleton-рядки -->
      <div
        v-if="store.listStatus === 'loading'"
        data-testid="masters-loading"
        aria-busy="true"
        class="flex flex-col gap-2"
      >
        <span class="sr-only">{{ t("masters.loading") }}</span>
        <Skeleton
          v-for="n in 3"
          :key="n"
          class="h-11 w-full"
        />
      </div>

      <!-- error: локальний inline-error + retry (НЕ full-page) -->
      <div
        v-else-if="store.listStatus === 'error'"
        data-testid="masters-error"
        class="flex flex-col gap-3"
      >
        <FormAlert
          variant="destructive"
          :message="t('masters.listError')"
        />
        <div>
          <Button
            variant="outline"
            size="sm"
            @click="store.load()"
          >
            {{ t("masters.retry") }}
          </Button>
        </div>
      </div>

      <!-- empty: центрований блок + CTA -->
      <div
        v-else-if="store.isEmpty"
        data-testid="masters-empty"
        class="flex flex-col items-center gap-3 py-10 text-center"
      >
        <UsersRound
          class="size-8 text-muted-foreground"
          aria-hidden="true"
        />
        <p class="font-medium text-foreground">
          {{ t("masters.empty.title") }}
        </p>
        <p class="max-w-sm text-sm text-muted-foreground">
          {{ t("masters.empty.body") }}
        </p>
        <Button as-child>
          <RouterLink :to="{ name: 'master-create' }">
            {{ t("masters.empty.cta") }}
          </RouterLink>
        </Button>
      </div>

      <!-- ready: список майстрів -->
      <template v-else>
        <ul
          data-testid="masters-rows"
          class="flex flex-col"
        >
          <MasterListRow
            v-for="master in store.masters"
            :key="master.id"
            :master="master"
            :target-hash="targetHash"
          />
        </ul>
      </template>
    </CardContent>
  </Card>

  <PageCTA v-if="store.listStatus === 'ready' && !store.isEmpty">
    <Button as-child>
      <RouterLink
        data-testid="btn-add-master-mobile"
        :to="{ name: 'master-create' }"
      >
        {{ t("masters.add") }}
      </RouterLink>
    </Button>
  </PageCTA>
</template>
